import axios from 'axios';
import { emitSessionExpired } from '../auth/sessionExpiry';
import { logActivity, ACTIVITY_REQUEST_MARKER } from '../admin/activityService';
import { reportAxiosModuleNotLicensed } from '../../licensing/moduleLockEvents';

/**
 * Unified API Client
 *
 * Single axios instance for ALL microservice calls. Uses window.location.origin
 * as the base URL — nginx in the frontend pod routes each path prefix to the
 * correct microservice:
 *
 *   /auth/api/*        → auth-microservice
 *   /policy/api/*      → saf3ai-policy-microservice
 *   /ops/api/*          → safe-monitoring
 *   /security/api/*     → safe-security
 *   /compliance/api/*   → safe-compliance
 *   /copilot/api/*      → saf3ai-copilot-ms
 *   /management/api/*   → saf3ai-management-microservice
 *   /finops/api/*       → saf3ai-finops-microservice
 *   /alerts/api/*       → saf3ai-alert-microservice
 *   /ai-explorer/api/*  → saf3ai-ai-microservice
 *   /gemini/api/*       → saf3ai-gemini-microservice
 *   /evaluation/api/*   → saf3ai-eval
 *   /search/api/*       → saf3ai-search
 *   /policy/api/*       → saf3ai-policy-microservice
 *   /sast/api/*         → saf3ai-sast-microservice
 *   /openai/api/*       → saf3ai-openai-microservice
 *   /redteam/*          → redteam
 *
 * For local development, set REACT_APP_API_ORIGIN in .env to override
 * (e.g., http://localhost:5002). Nginx is not involved locally — the
 * React dev server proxy or direct service URLs handle routing.
 */

const API_ORIGIN =
  (typeof window !== 'undefined' && window.env?.REACT_APP_API_ORIGIN) ||
  process.env.REACT_APP_API_ORIGIN ||
  (typeof window !== 'undefined' && window.location?.origin) ||
  '';

const api = axios.create({
  baseURL: API_ORIGIN,
  headers: { 'Content-Type': 'application/json' },
});

// ── Read-activity logging ──────────────────────────────────────────────────
// Successful GET requests are recorded as ACCESS events on the platform audit
// trail (Management → Audit Log), so "read a log / opened a resource" shows up
// alongside mutations. Noise is filtered and identical reads are de-duplicated
// to keep the volume sane.

// NOTE: order longest/most-specific prefixes first so e.g. /gemini-enterprise
// is matched before /gemini and /copilot-studio before /copilot.
const SERVICE_BY_PREFIX = [
  ['/gemini-enterprise/api', 'gemini-enterprise'],
  ['/copilot-studio/api', 'copilot-studio'],
  ['/auth/api', 'auth'],
  ['/management/api', 'management'],
  ['/finops/api', 'finops'],
  ['/graph/api', 'graph-lens'],
  ['/ops/api', 'monitoring'],
  ['/alerts/api', 'alert'],
  ['/security/api', 'security'],
  ['/compliance/api', 'compliance'],
  ['/copilot/api', 'copilot'],
  ['/gemini/api', 'gemini'],
  ['/ai-explorer/api', 'ai'],
  ['/evaluation/api', 'evaluation'],
  ['/search/api', 'search'],
  ['/sast/api', 'sast'],
  ['/openai/api', 'openai'],
  ['/google_antigravity/api', 'google_antigravity'],
  ['/policy/api', 'policy'],
  ['/redteam', 'redteam'],
];

// Substrings that mark a GET as noise (polling, auth plumbing, the audit page
// reading itself). These are NOT recorded as reads. Kept deliberately specific
// so meaningful resource reads (e.g. Gemini "/sessions", "/posture/metrics")
// are still captured.
const READ_DENY = [
  '/audit-log', '/activity', '/health', 'healthz', '/openapi', '/docs',
  'feature-flag', 'permission', 'token/refresh', 'notification',
  'heartbeat', '/ping',
];

const READ_DEDUP_MS = 15000;
const _readDedup = new Map();

const serviceForPath = (pathname) => {
  const hit = SERVICE_BY_PREFIX.find(([p]) => pathname.startsWith(p));
  return hit ? hit[1] : 'platform';
};

const resourceForPath = (pathname) => {
  // Strip the "/<service>/api/vN/" prefix, then read the resource segment(s).
  const stripped = pathname.replace(/^\/[^/]+\/api(\/v\d+)?\//, '').replace(/^\/+/, '');
  const segs = stripped.split('/').filter(Boolean);
  const resourceType = segs[0] || 'resource';
  const tail = segs.length > 1 ? segs[segs.length - 1] : undefined;
  // Treat a trailing numeric / uuid-ish segment as the resource id.
  const resourceId = tail && /(^\d+$)|([0-9a-f-]{6,})/i.test(tail) ? tail : undefined;
  return { resourceType, resourceId };
};

const maybeLogRead = (response) => {
  try {
    const config = response?.config || {};
    if ((config.method || '').toLowerCase() !== 'get') return;
    // Skip the activity-logging request itself (defensive; it uses a separate
    // instance, but guard against any future routing through this client).
    if (config.params && config.params[ACTIVITY_REQUEST_MARKER]) return;

    let pathname = config.url || '';
    try {
      pathname = new URL(pathname, API_ORIGIN || window.location.origin).pathname;
    } catch {
      // already a path
    }
    const lower = pathname.toLowerCase();
    if (READ_DENY.some((d) => lower.includes(d))) return;

    const now = Date.now();
    const last = _readDedup.get(pathname);
    if (last && now - last < READ_DEDUP_MS) return;
    _readDedup.set(pathname, now);
    if (_readDedup.size > 500) _readDedup.clear();

    const { resourceType, resourceId } = resourceForPath(pathname);
    logActivity({
      action: 'ACCESS',
      method: 'GET',
      resourceType,
      resourceId,
      path: pathname,
      service: serviceForPath(pathname),
      statusCode: response.status,
    });
  } catch {
    // Never let activity logging break the real response.
  }
};

// Services that adopt the caller's X-Request-ID (Atlas error_codes.py) and show it beside errors.
const REQUEST_REF_PATHS = ['/agent-atlas/api/v1/finops', '/agent-atlas/api/v1/metrics'];

const newRequestRef = () => {
  const bytes = new Uint8Array(6);
  const cryptoApi = typeof window !== 'undefined' ? window.crypto : undefined;
  if (cryptoApi?.getRandomValues) cryptoApi.getRandomValues(bytes);
  else bytes.forEach((_, i) => { bytes[i] = Math.floor(Math.random() * 256); });
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
};

// Auth token + organization context
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('jwt_token') || localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Add organization context — required by ops, management, policy, and other services
    try {
      const rawUser = localStorage.getItem('user');
      if (rawUser) {
        const user = JSON.parse(rawUser);
        const orgId = user?.organization_id || user?.org_id || user?.organizationId;
        const userId = user?.user_id || user?.id || user?.sub || user?.email;
        if (orgId) {
          // Query param (ops, management services expect this)
          config.params = {
            ...config.params,
            organization_id: config.params?.organization_id ?? orgId,
          };
          // Headers (policy, endpoint services expect these)
          config.headers['X-Organization-ID'] = String(orgId);
        }
        if (userId) {
          config.headers['X-User-ID'] = String(userId);
        }
        if (user?.tenant_id) {
          config.headers['X-Tenant-ID'] = String(user.tenant_id);
        }
      }
    } catch {
      // Non-fatal — requests can still proceed with token-only auth
    }

    // A request ref the page can show beside an error even when no answer came back (an
    // ingress timeout): the service logs under this id. Only where the service adopts it.
    if (REQUEST_REF_PATHS.some((p) => (config.url || '').includes(p)) && !config.headers['X-Request-ID']) {
      config.headers['X-Request-ID'] = newRequestRef();
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Error handling
api.interceptors.response.use(
  (response) => {
    maybeLogRead(response);
    return response;
  },
  (error) => {
    // A 403 module_not_licensed swaps the page for its lock screen (ModuleLockBoundary).
    reportAxiosModuleNotLicensed(error);
    if (!error.response) {
      error.userMessage = error.code === 'ECONNABORTED'
        ? 'Request timeout. The server is taking too long to respond.'
        : 'Network error. Unable to reach the server.';
      return Promise.reject(error);
    }

    const status = error.response.status;

    if (status === 401) {
      const isLoginRequest = error.config?.url?.includes('/login');
      if (!isLoginRequest) {
        emitSessionExpired('Your session has expired. Continue your current session or sign in again.');
        error.userMessage = 'Your session has expired.';
      }
    } else if (status >= 500) {
      error.userMessage = `Server error (${status}). Please try again later.`;
    } else if (status >= 400) {
      error.userMessage = error.response?.data?.detail ||
        error.response?.data?.message ||
        error.response?.data?.error ||
        `Request failed with status ${status}`;
    }

    return Promise.reject(error);
  }
);

export default api;

// Companion instance for genuinely-public endpoints (magic-link invite
// preview/redeem, password-reset, etc). NO auth interceptor — these routes
// must work for users who are not logged in to SAF3AI. Response interceptor
// is also skipped because 401 here doesn't mean "your session expired"; it
// usually means the token in the URL is bogus and should be shown inline.
export const unauthApi = axios.create({
  baseURL: API_ORIGIN,
  headers: { 'Content-Type': 'application/json' },
});
